import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProvided,
  DropResult,
} from '@hello-pangea/dnd';

import { BsLayers } from 'react-icons/bs';
import { cn } from '@ais-ui/utils';
import {
  Button,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Label,
  Popover,
  PopoverDragNDrop,
  PopoverTrigger,
  ScrollArea,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import {
  getIconFromLayerType,
  getTextFromLayerType,
  LayerItem,
  useLayers,
} from '@ais-ui/react-openlayers';
import { useLocalization } from '@ais-ui/locale-management';
import {
  PiMagnifyingGlassPlusThin,
  PiStarFill,
  PiTagFill,
  PiTagThin,
} from 'react-icons/pi';
import { WMSLayers } from './Actions/WMSLayers';
import { Bookmarks } from './Actions/Bookmarks';
import { BaseMaps } from './Actions/BaseMaps';
import VectorLayer from 'ol/layer/Vector';
import { X } from 'lucide-react';
import { noop } from 'lodash';
import { FilterBadge } from '../FilterTargets/FilterShips';
import { SimpleFilter, useFilterContext } from '../FilterContext';
import { useMapLegendSettings } from '../ColorTargetsBy/ColorBy/useMapLegendSettings';
import { VectorTiles } from './Actions/VectorTiles';
import { Filter } from '@ais-ui/websockets';
import { isWmsLayerShownOnStartUp } from '../../WMSLayers/useWMSSettings';
import { useSettings } from '../../Settings/SettingsProvider';
import { isShapeShownOnStartUp } from '../../Shapes/isShapeShownOnStartUp';
import { Coverage } from './Actions/Coverage';
import { areSourceIdentifiersShowOnStartUp } from '../Coverage/areSourceIdentifiersShownOnStartUp';
import { GPSLost } from './Actions/GPSLost';
import { GPSLostLayerActions } from './GPSLostLayerActions';
import { GPSLostSelectedShips } from './GPSLostSelectedShips';
import OlGeometry from 'ol/geom/Geometry';
import Feature from 'ol/Feature';
import { AppearanceControl } from './AppearanceControl';

interface LayerControlProps {
  className?: string;
}

interface DragNDropLayerProps {
  layer: LayerItem;
  index: number;
}

interface ShowLabelsButtonProps {
  layer: LayerItem;
  className?: string;
}

const ShowLabelsButton = React.forwardRef<
  HTMLButtonElement,
  ShowLabelsButtonProps
>(({ layer, className }, ref) => {
  const { toggleLabels } = useLayers();
  const [labelsAreActive, setLabelsAreActive] = useState(
    layer?.properties?.showLabel
  );

  useEffect(() => {
    if (layer?.properties?.showLabel) {
      setLabelsAreActive(true);
    } else {
      setLabelsAreActive(false);
    }
  }, [layer]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          ref={ref}
          size="icon"
          variant="ghost"
          className={cn('h-9 w-9 flex-shrink-0 p-0 m-0', className)}
          onClick={() => {
            toggleLabels(layer);
          }}
        >
          {labelsAreActive ? (
            <PiTagFill className="h-5 w-5" />
          ) : (
            <PiTagThin className="h-5 w-5" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>Toggle labels</TooltipContent>
    </Tooltip>
  );
});

ShowLabelsButton.displayName = 'ShowLabelsButton';

const DragNDropLayer = React.forwardRef<HTMLDivElement, DragNDropLayerProps>(
  ({ layer, index }, ref) => {
    const { messages } = useLocalization();
    const {
      toggleLayerVisibility,
      removeLayer,
      zoomToLayer,
      zoomToExtent,
      getLayersByIdContaining,
    } = useLayers();

    const [disableDragNnDropTooltip, setDisableDragNnDropTooltip] =
      useState(false);

    const { settings } = useSettings();
    const { onDisableAll, onEnableAll } = useMapLegendSettings();

    const LayerIcon = useMemo(
      () => getIconFromLayerType(layer.type),
      [layer.type]
    );
    const layerTypeTooltip = useMemo(
      () => getTextFromLayerType(layer.type),
      [layer.type]
    );

    const isFeatureLayer = useMemo(() => {
      return (
        layer.type === 'circle' ||
        layer.type === 'point' ||
        layer.type === 'polygon' ||
        layer.type === 'linestring' ||
        layer.type === 'featurecollection'
      );
    }, [layer.type]);

    const isStartUpLayer = useMemo(() => {
      if (layer.type === 'wms') {
        return isWmsLayerShownOnStartUp(settings, layer.id);
      }
      if (
        layer.type === 'circle' ||
        layer.type === 'point' ||
        layer.type === 'polygon' ||
        layer.type === 'linestring' ||
        layer.type === 'featurecollection'
      ) {
        return isShapeShownOnStartUp(settings, layer.id);
      }

      if (layer.type === 'source-identifiers') {
        return areSourceIdentifiersShowOnStartUp(settings);
      }

      return false;
    }, [layer.type, settings, layer.id]);

    const onRemoveLayer = useCallback(
      (layer: LayerItem) => {
        removeLayer(layer.layer);
        if (layer.type === 'coverage' && layer.properties?.id) {
          const event = new CustomEvent('removeCoverage', {
            detail: { id: layer.properties.id },
          });
          window.dispatchEvent(event);
        } else if (layer.type === 'gps-lost-grid') {
          const event = new CustomEvent('removeGPSLostGrid', {
            detail: { id: layer.properties.id },
          });

          const gpsLostLayers = getLayersByIdContaining('gps-lost-track');
          gpsLostLayers.forEach((layer) => {
            removeLayer(layer.layer);
          });

          window.dispatchEvent(event);
        }
      },
      [removeLayer, layer.type, getLayersByIdContaining]
    );

    const onZoomToLayer = useCallback(
      (layer: LayerItem) => {
        if (layer.layer instanceof VectorLayer) {
          // Use zoomToLayer for vector layers
          zoomToLayer(layer.layer as VectorLayer<Feature<OlGeometry>>);
        } else if (layer.type === 'coverage' && layer.properties?.extent) {
          // Zoom to the extent provided in properties.extent for coverage layers
          const extent = layer.properties.extent;
          zoomToExtent(extent);
        } else {
          console.warn(
            'Cannot zoom to layer. Unsupported layer type or missing extent:',
            layer
          );
        }
      },
      [zoomToLayer, layer.type]
    );

    if (layer.isBase) {
      return;
    }

    if (layer.type === 'gps-lost-track-linestring') {
      return;
    }

    if (layer.type === 'gps-lost-track-point') {
      return;
    }

    return (
      <Draggable draggableId={layer.id} index={index}>
        {(provided, snapshot) => {
          const isDragging = snapshot.isDragging;

          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  className={cn(
                    'flex items-center justify-between px-2 transition-transform duration-200',
                    isDragging &&
                      'absolute transform scale-105 bg-gray-200 shadow-lg rounded-md cursor-grabbing z-50',
                    !isDragging &&
                      'bg-white hover:bg-gray-200 rounded-lg hover:cursor-move hover:shadow-lg'
                  )}
                >
                  <div className="w-full h-10 flex items-center justify-between relative">
                    <div className="flex items-center space-x-4">
                      <Checkbox
                        checked={layer.isVisible}
                        onCheckedChange={(value) => {
                          if (layer.name.toLowerCase() === 'ships') {
                            if (layer.isVisible) {
                              onDisableAll();
                            } else {
                              onEnableAll();
                            }
                          }
                          toggleLayerVisibility(layer.layer, value as boolean);
                        }}
                        id={layer.id}
                      />
                      <div className="flex items-center space-x-2">
                        <Tooltip>
                          <TooltipTrigger className="hover:cursor-default">
                            <div className="relative">
                              <LayerIcon className={cn('h-4 w-4')}></LayerIcon>
                              {isStartUpLayer && (
                                <PiStarFill className="absolute h-2 w-2 text-yellow-400 -top-1.5 -right-1.5" />
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>{layerTypeTooltip}</TooltipContent>
                        </Tooltip>
                        <Label
                          className={cn(
                            'text-sm font-medium cursor-move truncate max-w-[200px] block',
                            isDragging && 'cursor-grabbing',
                            !isDragging && 'hover:cursor-move'
                          )}
                          htmlFor={layer.id}
                        >
                          {layer.name}
                        </Label>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      {layer.canZoomToFromLayerControl && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              id={`zoom-to-layer-${layer.id}`}
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9 flex-shrink-0 p-0 m-0"
                              onClick={() => onZoomToLayer(layer)}
                            >
                              <PiMagnifyingGlassPlusThin className="h-5 w-5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Zoom to layer</TooltipContent>
                        </Tooltip>
                      )}
                      {!isFeatureLayer && layer.canShowLabel && (
                        <ShowLabelsButton layer={layer}></ShowLabelsButton>
                      )}
                      {isFeatureLayer && (
                        <AppearanceControl
                          layer={layer}
                          canToggleLabels={true}
                          canChangeColor={true}
                          canChangeTransparency={true}
                          canChangeSize={false}
                        ></AppearanceControl>
                      )}
                      {layer.type === 'gps-lost-grid' && (
                        <GPSLostSelectedShips></GPSLostSelectedShips>
                      )}
                      {layer.isRemovableFromLayerControl && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              id={`remove-layer-${layer.id}`}
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9 flex-shrink-0 p-0 m-0"
                              onClick={() => onRemoveLayer(layer)}
                            >
                              <X className="h-5 w-5 opacity-50" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Remove layer</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    {layer.type === 'gps-lost-grid' && (
                      <GPSLostLayerActions
                        setDisableDragNnDropTooltip={
                          setDisableDragNnDropTooltip
                        }
                        isDragging={isDragging}
                      ></GPSLostLayerActions>
                    )}
                  </div>
                </div>
              </TooltipTrigger>
              {!disableDragNnDropTooltip && (
                <TooltipContent>
                  {messages.layerControlOnHoverDragNDropTooltip}
                </TooltipContent>
              )}
            </Tooltip>
          );
        }}
      </Draggable>
    );
  }
);

DragNDropLayer.displayName = 'DragNDropLayer';

interface FilterBadgesProps {
  allFilters: (Filter | SimpleFilter)[];
}

const FilterBadges = ({ allFilters }: FilterBadgesProps) => {
  return (
    <div className="grid grid-rows-2 grid-flow-col gap-x-1 pointer-events-auto">
      {allFilters.map((filter) => {
        const isFilter = (filter: Filter | SimpleFilter): filter is Filter => {
          return (filter as Filter).uuid !== undefined;
        };

        // Use the type guard to check the type of filter
        const key = isFilter(filter) ? filter.uuid : filter.id;

        return <FilterBadge key={key} filter={filter} />;
      })}
    </div>
  );
};

export const LayerControl = React.memo(
  React.forwardRef<HTMLDivElement, LayerControlProps>(({ className }, ref) => {
    const { messages } = useLocalization();
    const [open, setOpen] = useState(false);
    const { layers, rearrangeLayer } = useLayers();

    const filterContext = useFilterContext();

    const allFilters = useMemo(
      () => [...filterContext.simpleFilters, ...filterContext.customFilters],
      [filterContext.simpleFilters, filterContext.customFilters]
    );

    const onDragEnd = useCallback(
      (result: DropResult) => {
        if (!result.destination) return;

        const sourceIndex = layers.length - result.source.index - 1;
        const destinationIndex = layers.length - result.destination.index - 1;
        const movedLayer = layers[sourceIndex];

        rearrangeLayer(movedLayer, destinationIndex);
      },
      [layers, rearrangeLayer]
    );

    const handleToggle = useCallback(() => {
      setOpen(!open);
    }, [open]);

    const handleClose = useCallback(() => {
      setOpen(false);
    }, []);

    const reversedLayers = useMemo(() => layers.slice().reverse(), [layers]);

    const visibleLayers = useMemo(() => {
      return layers.filter((layer) => {
        if (layer.isBase) return false;
        if (layer.type === 'gps-lost-track-linestring') return false;
        if (layer.type === 'gps-lost-track-point') return false;
        return true;
      });
    }, [layers]);

    const contentHeight = useMemo(() => {
      if (visibleLayers.length === 0) return 'auto';

      // Each layer is likely around 36-40px, let's try 36px first
      const layerHeight = 36;
      const gapSize = 12;
      const bottomPadding = 12; // only p-2 bottom since pt-0

      // Calculate total height: (layers * height) + (gaps between layers) + bottom padding
      const totalHeight =
        visibleLayers.length * layerHeight +
        (visibleLayers.length - 1) * gapSize +
        bottomPadding;

      // Cap at 384px (max-h-96)
      return Math.min(totalHeight, 384);
    }, [visibleLayers.length]);

    return (
      <div className="flex space-x-2 h-14 h-max-14">
        <Popover open={open} onOpenChange={noop}>
          <div className="relative flex space-x-2">
            <Tooltip>
              <PopoverTrigger asChild>
                <TooltipTrigger asChild>
                  <Button
                    id="layer-control-button"
                    onClick={handleToggle}
                    variant="outline"
                    size="icon"
                    className={cn(
                      'flex self-end items-center justify-center h-14 w-14 p-2 group/btn rounded-lg',
                      className
                    )}
                  >
                    <BsLayers className="h-9 w-9 group-hover/btn:text-primary" />
                  </Button>
                </TooltipTrigger>
              </PopoverTrigger>
              <TooltipContent>
                {messages.mapLayerControlButtonTooltip}
              </TooltipContent>
            </Tooltip>
            {open && (
              <PopoverDragNDrop
                className={cn(
                  'absolute bottom-0 left-14 px-2 py-4 w-[424px]',
                  className
                )}
              >
                <CardHeader className="p-2 space-y-6">
                  <div>
                    <div className="flex justify-between">
                      <CardTitle className="text-lg font-medium pb-2">
                        Actions
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="-mt-5 -mr-3"
                        onClick={handleClose}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                          className="h-4 w-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </Button>
                    </div>
                    <Separator />
                    <div className="flex space-x-4 pt-4">
                      <Bookmarks />
                      <BaseMaps />
                      <WMSLayers />
                      <VectorTiles />
                      <Coverage></Coverage>
                      <GPSLost />
                    </div>
                  </div>
                  <div>
                    <CardTitle className="text-lg font-medium">
                      {messages.layerControlTitle}
                    </CardTitle>
                    <Separator className="mt-2" />
                  </div>
                </CardHeader>
                <CardContent
                  className="max-h-96 p-2 pt-0"
                  style={{ height: contentHeight }}
                >
                  <ScrollArea className="h-full">
                    <DragDropContext onDragEnd={onDragEnd}>
                      <Droppable droppableId="layers" type="task">
                        {(provided: DroppableProvided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="grid gap-2 mr-2"
                          >
                            {reversedLayers.map((layer, index) => (
                              <DragNDropLayer
                                key={layer.id}
                                layer={layer}
                                index={index}
                              />
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </ScrollArea>
                </CardContent>
              </PopoverDragNDrop>
            )}
          </div>
        </Popover>
        {!open && allFilters.length > 0 && (
          <FilterBadges allFilters={allFilters} />
        )}
      </div>
    );
  })
);

// Add a display name for better debugging
LayerControl.displayName = 'LayerControl';
